import { useEffect, useMemo, useState } from 'react';
import { Button, Popconfirm, Space } from 'antd';
import _ from 'lodash';
import VoucherService from 'services/voucher';
import { formatCurrency } from 'utils/number';
import { formatDate } from 'utils/date';
import { useApp } from 'contexts/app';

const service = new VoucherService();

const toBoolean = (value, fallback = false) => {
  if (value === undefined || value === null) return fallback;
  if (value === true || value === 1 || value === '1' || value === 'true') return true;
  if (value === false || value === 0 || value === '0' || value === 'false') return false;
  return Boolean(value);
};

const useList = () => {
  const { showNotify } = useApp();
  const [data, setData] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [voucherID, setVoucherID] = useState(false);

  const loadData = async (info) => {
    const list = await service.list({ info });
    const normalizedList = (Array.isArray(list) ? list : []).map((item) => ({
      ...item,
      is_active: toBoolean(item?.is_active, true),
      is_public: toBoolean(item?.is_public, true),
    }));
    setData(normalizedList);
  };

  useEffect(() => {
    loadData();
  }, []);

  const onSearch = (text) => {
    loadData(text);
  };

  const searchDelay = useMemo(() => _.debounce(onSearch, 500), []);

  useEffect(() => {
    return () => {
      searchDelay.cancel();
    };
  }, [searchDelay]);

  const openVoucher = (id) => {
    setVoucherID(id);
    setIsModalOpen(true);
  };

  const onSearchChange = (e) => {
    searchDelay(e.target.value);
  };

  const copyVoucherCode = async (text) => {
    await window.navigator.clipboard.writeText(text);
    showNotify(`Đã copy ${text}`);
  };

  const deleteVoucher = async (id) => {
    await service.delete(id);
    loadData();
  };

  const onVoucherSaved = () => {
    loadData();
  };

  const columns = [
    {
      title: 'Voucher',
      render: (record) => (
        <Space>
          <Button onClick={() => openVoucher(record.id)} type='link'>{record.code}</Button>
          <Button type='link' onClick={() => copyVoucherCode(record.code)}>copy</Button>
        </Space>
      ),
    },
    {
      title: 'Mô tả',
      render: (record) => record.description,
    },
    {
      title: 'Ngày tạo',
      render: (record) => formatDate(record.create_on),
    },
    {
      title: 'Giá trị',
      render: (record) => {
        if (record.type === 'fixed') {
          return formatCurrency(record.value);
        }
        return `${formatCurrency(record.value)} %`;
      },
    },
    {
      title: 'Điều kiện',
      render: (record) => (
        <Space direction='vertical' size={0}>
          <div>Đơn tối thiểu: {formatCurrency(record?.min_order_amount || 0)}</div>
          <div>
            Giảm tối đa: {record?.max_discount_amount ? formatCurrency(record.max_discount_amount) : 'Không giới hạn'}
          </div>
          <div>
            Mỗi khách: {record?.usage_limit_per_customer || 1} lần
          </div>
        </Space>
      ),
    },
    {
      title: 'Trạng thái',
      render: (record) => (
        <Space direction='vertical' size={0}>
          <div>{record?.is_active ? 'Đang hoạt động' : 'Tạm khoá'}</div>
          <div>{record?.is_public ? 'Công khai' : 'Nội bộ'}</div>
          <div>
            {record?.limit?.time?.is_limit
              ? `${formatDate(record?.limit?.time?.start)} - ${formatDate(record?.limit?.time?.end)}`
              : 'Không giới hạn thời gian'}
          </div>
        </Space>
      ),
    },
    {
      title: 'Sử dụng',
      render: (record) => (
        <Space direction='vertical'>
          <div>Đã sử dụng {(record.usages || []).length} / {record.usage_limit}</div>
          {(record.usages || []).map((usage) => (
            <div key={usage.order_id}>{usage.order_id}</div>
          ))}
        </Space>
      ),
    },
    {
      title: '',
      render: (record) => (
        <Space direction='vertical'>
          <Popconfirm
            title='Xoá'
            description='Bạn có chắc chăn muốn xoá?'
            onConfirm={() => deleteVoucher(record.id)}
            okText='Yes'
            cancelText='No'
          >
            <Button type='link'>Delete</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return {
    data,
    isModalOpen,
    voucherID,
    setIsModalOpen,
    openVoucher,
    onSearch,
    onSearchChange,
    onVoucherSaved,
    columns,
    copyVoucherCode,
    deleteVoucher,
  };
};

export default useList;